import {
  Alert,
  Badge,
  EmptyState,
  ErrorState,
  Icon,
  IconButton,
  Inline,
  PageHeader,
  SegmentedControl,
  Skeleton,
  Table,
  Toolbar,
  badgeToneForStatus,
} from "@evolua/ui";
import { EvoluaApiError } from "@evolua/api-client";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useManagerVacationCalendar } from "../api/use-manager.js";

function shiftMonth(isoDay: string, delta: number): string {
  const d = new Date(`${isoDay}T00:00:00.000Z`);
  d.setUTCMonth(d.getUTCMonth() + delta);
  d.setUTCDate(1);
  return d.toISOString().slice(0, 10);
}

function shiftQuarter(isoDay: string, delta: number): string {
  const d = new Date(`${isoDay}T00:00:00.000Z`);
  d.setUTCMonth(d.getUTCMonth() + delta * 3);
  const q = Math.floor(d.getUTCMonth() / 3) * 3;
  d.setUTCMonth(q, 1);
  return d.toISOString().slice(0, 10);
}

const VIEW_ITEMS = [
  { id: "month", label: "Mês" },
  { id: "quarter", label: "Trimestre" },
] as const;

/** Calendário de férias da equipe (FASE-06-T45). */
export function CalendarioFeriasPage() {
  const [view, setView] = useState<"month" | "quarter">("month");
  const [anchor, setAnchor] = useState("2026-10-01");
  const query = useMemo(
    () => ({ from: anchor, view, minPresentPercent: 50 }),
    [anchor, view],
  );
  const cal = useManagerVacationCalendar(query);

  const correlationId =
    cal.error instanceof EvoluaApiError ? cal.error.problem.traceId : undefined;

  function onPrev() {
    setAnchor((a) => (view === "month" ? shiftMonth(a, -1) : shiftQuarter(a, -1)));
  }
  function onNext() {
    setAnchor((a) => (view === "month" ? shiftMonth(a, 1) : shiftQuarter(a, 1)));
  }

  return (
    <>
      <PageHeader
        title="Calendário de férias"
        lead={`Períodos aprovados e solicitados da sua equipe, com sobreposições e alerta de cobertura mínima (${cal.data?.minPresentPercent ?? 50}% presente).`}
      />

      <Toolbar>
        <Inline gap={2}>
          <IconButton label="Período anterior" onClick={onPrev}>
            <Icon name="chevron-left" size={18} />
          </IconButton>
          <IconButton label="Próximo período" onClick={onNext}>
            <Icon name="chevron-right" size={18} />
          </IconButton>
        </Inline>
        <SegmentedControl
          ariaLabel="Visualização do calendário"
          items={VIEW_ITEMS}
          value={view}
          onChange={(id) => setView(id as "month" | "quarter")}
        />
        {cal.data ? (
          <Badge tone={cal.data.hasCoverageAlert ? "warning" : "neutral"}>
            {cal.data.from} → {cal.data.to}
          </Badge>
        ) : null}
      </Toolbar>

      {cal.isPending ? (
        <Skeleton height="8rem" label="Calendário" />
      ) : cal.isError ? (
        <ErrorState
          {...(correlationId ? { correlationId } : {})}
          onRetry={() => void cal.refetch()}
        />
      ) : !cal.data || cal.data.events.length === 0 ? (
        <EmptyState
          title="Sem férias no período"
          description="Nenhum período aprovado ou solicitado nesta janela."
        />
      ) : (
        <div className="evolua-stack evolua-stack--3">
          {cal.data.hasCoverageAlert ? (
            <Alert tone="warning">
              Alerta: em alguns dias a equipe fica abaixo de {cal.data.minPresentPercent}%
              presente. Aprovar novos pedidos sobrepostos exigirá confirmação (force).
            </Alert>
          ) : null}

          <section>
            <h2 className="evolua-section-title">Períodos</h2>
            <Table
              caption="Períodos"
              getRowId={(e) => e.id}
              columns={[
                { id: "who", header: "Colaborador", cell: (e) => e.employeeName },
                {
                  id: "status",
                  header: "Status",
                  cell: (e) => (
                    <>
                      <Badge tone={badgeToneForStatus(e.status)}>
                        {e.status === "APPROVED" ? "Aprovado" : "Solicitado"}
                      </Badge>
                      {e.overlaps ? <Badge tone="warning">Sobreposição</Badge> : null}
                    </>
                  ),
                },
                {
                  id: "period",
                  header: "Período",
                  cell: (e) => `${e.startDate} → ${e.endDate}`,
                },
              ]}
              rows={cal.data.events}
            />
          </section>

          {cal.data.overlaps.length > 0 ? (
            <section>
              <h2 className="evolua-section-title">Sobreposições</h2>
              <Table
                caption="Sobreposições"
                getRowId={(o) => `${o.aId}-${o.bId}`}
                columns={[
                  {
                    id: "range",
                    header: "Intervalo",
                    cell: (o) => `${o.overlapStart} → ${o.overlapEnd}`,
                  },
                ]}
                rows={cal.data.overlaps}
              />
            </section>
          ) : null}

          <section>
            <h2 className="evolua-section-title">Dias com alerta</h2>
            <Table
              caption="Dias com alerta"
              getRowId={(d) => d.date}
              columns={[
                { id: "date", header: "Dia", cell: (d) => d.date },
                {
                  id: "present",
                  header: "Presente",
                  align: "numeric",
                  cell: (d) => `${d.presentPercent}%`,
                },
                {
                  id: "vacation",
                  header: "Em férias",
                  align: "numeric",
                  cell: (d) => d.onVacationCount,
                },
              ]}
              rows={cal.data.days.filter((d) => d.belowMinPresence).slice(0, 12)}
            />
          </section>
        </div>
      )}

      <p className="evolua-mt">
        <Link to="/aprovacoes">Fila de aprovações →</Link>
      </p>
    </>
  );
}
